import { useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Phone } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'

export default function LoginPage() {
  const { session, loadingAuth, signIn } = useAuth()
  const location = useLocation()
  const from = location.state?.from?.pathname || '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  if (!loadingAuth && session) {
    return <Navigate to={from} replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    try {
      await signIn(email.trim(), password)
      toast.success('Login realizado')
    } catch (err) {
      toast.error(err?.message || 'Falha no login')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen w-full min-w-0 bg-background">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl items-center justify-center px-4 py-10">
        <div className="grid w-full max-w-4xl overflow-hidden rounded-3xl border border-border bg-card shadow-lg lg:grid-cols-[1.05fr_1fr]">
          <section className="hidden border-r border-border bg-muted/20 p-8 lg:flex lg:flex-col lg:justify-between">
            <div>
              <div className="flex items-center gap-0">
                <img src="/icon-sm.png" alt="WappDesk" className="h-12 w-12 object-contain" />
                <div>
                  <p className="font-semibold tracking-tight text-foreground">
                    <span className="text-lg">WappDesk</span>{' '}
                    <span className="text-xs font-medium text-muted-foreground">Voice</span>
                  </p>
                  <p className="text-xs text-muted-foreground">Login seguro</p>
                </div>
              </div>
              <h1 className="mt-8 text-3xl font-semibold leading-tight text-foreground">
                Gerencie sua operacao de voz em um painel simples.
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Entre com as mesmas credenciais do WappDesk e continue seu fluxo sem friccao.
              </p>
            </div>
          </section>

          <section className="p-6 sm:p-8">
            <div className="mb-6 flex items-center justify-center lg:hidden">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-wappdesk-primary/15 text-wappdesk-primary">
                <Phone className="h-9 w-9" aria-hidden />
              </div>
            </div>

            <div className="mb-6 text-center lg:text-left">
              <h2 className="text-2xl font-bold text-foreground">Entrar</h2>
              <p className="mt-1 text-sm text-muted-foreground">Use sua conta existente do WappDesk.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground">E-mail</span>
                <input
                  id="voice-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-h-[44px] w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground">Senha</span>
                <div className="relative">
                  <input
                    id="voice-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="min-h-[44px] w-full rounded-lg border border-input bg-background px-3 py-2.5 pr-10 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-2 inline-flex items-center text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </label>

              <button
                type="submit"
                disabled={submitting || loadingAuth}
                className="min-h-[44px] w-full rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50 touch-manipulation"
              >
                {submitting ? 'Entrando...' : 'Entrar'}
              </button>
            </form>

            <p className="mt-5 text-center text-sm text-muted-foreground">
              Nao tem conta?{' '}
              <Link to="/register" className="font-medium text-primary hover:underline">
                Registre-se
              </Link>
            </p>

            <p className="mt-4 text-center text-xs text-muted-foreground">
              Acesse sua conta para gerenciar planos, numeros e creditos em um so lugar.
            </p>

          </section>
        </div>
      </div>
    </div>
  )
}
